import { useState } from 'react';
import { 
  Building2, MapPin, Home, DollarSign, Users, Bed, Bath, 
  Ruler, FileText, CheckSquare, Map, Calendar, Shield
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';

// Property types
const propertyTypes = [
  { id: 'apartment', ar: 'شقة', en: 'Apartment' },
  { id: 'villa', ar: 'فيلا', en: 'Villa' },
  { id: 'townhouse', ar: 'تاون هاوس', en: 'Townhouse' },
  { id: 'penthouse', ar: 'بنتهاوس', en: 'Penthouse' },
  { id: 'studio', ar: 'استوديو', en: 'Studio' },
  { id: 'land', ar: 'أرض', en: 'Land' },
  { id: 'building', ar: 'مبنى', en: 'Building' },
  { id: 'warehouse', ar: 'مستودع', en: 'Warehouse' },
  { id: 'office', ar: 'مكتب', en: 'Office' },
  { id: 'shop', ar: 'محل تجاري', en: 'Shop' }
];

// Transaction types
const transactionTypes = [
  { id: 'sale', ar: 'للبيع', en: 'For Sale' },
  { id: 'rent', ar: 'للإيجار', en: 'For Rent' },
  { id: 'daily_rent', ar: 'إيجار يومي', en: 'Daily Rent' }
];

// Ownership types
const ownershipTypes = [
  { id: 'freehold', ar: 'تملك حر', en: 'Freehold' },
  { id: 'leasehold', ar: 'حق انتفاع', en: 'Leasehold' }
];

// Furnishing options
const furnishingOptions = [
  { id: 'furnished', ar: 'مفروش', en: 'Furnished' },
  { id: 'semi_furnished', ar: 'شبه مفروش', en: 'Semi Furnished' },
  { id: 'unfurnished', ar: 'غير مفروش', en: 'Unfurnished' }
];

// Emirates list
const emirates = [
  { id: 'dubai', ar: 'دبي', en: 'Dubai' },
  { id: 'abudhabi', ar: 'أبوظبي', en: 'Abu Dhabi' },
  { id: 'sharjah', ar: 'الشارقة', en: 'Sharjah' },
  { id: 'ajman', ar: 'عجمان', en: 'Ajman' },
  { id: 'rak', ar: 'رأس الخيمة', en: 'Ras Al Khaimah' },
  { id: 'fujairah', ar: 'الفجيرة', en: 'Fujairah' },
  { id: 'uaq', ar: 'أم القيوين', en: 'Umm Al Quwain' }
];

// Amenities
const amenities = [
  { id: 'parking', ar: 'موقف سيارات', en: 'Parking' },
  { id: 'gym', ar: 'صالة رياضية', en: 'Gym' },
  { id: 'pool', ar: 'مسبح', en: 'Swimming Pool' },
  { id: 'security', ar: 'أمن 24/7', en: '24/7 Security' },
  { id: 'garden', ar: 'حديقة', en: 'Garden' },
  { id: 'balcony', ar: 'شرفة', en: 'Balcony' },
  { id: 'maid_room', ar: 'غرفة خادمة', en: 'Maid Room' },
  { id: 'storage', ar: 'غرفة تخزين', en: 'Storage Room' },
  { id: 'central_ac', ar: 'تكييف مركزي', en: 'Central A/C' },
  { id: 'concierge', ar: 'خدمة كونسيرج', en: 'Concierge' },
  { id: 'children_area', ar: 'منطقة ألعاب أطفال', en: 'Children Play Area' },
  { id: 'bbq', ar: 'منطقة شواء', en: 'BBQ Area' },
  { id: 'sea_view', ar: 'إطلالة بحرية', en: 'Sea View' },
  { id: 'city_view', ar: 'إطلالة على المدينة', en: 'City View' },
  { id: 'pets_allowed', ar: 'مسموح بالحيوانات', en: 'Pets Allowed' },
  { id: 'kitchen_appliances', ar: 'أجهزة مطبخ', en: 'Kitchen Appliances' }
];

export default function RealEstateForm({ onDataChange, initialData = {} }) {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  
  // Form data
  const [formData, setFormData] = useState({
    propertyType: initialData.propertyType || '',
    transactionType: initialData.transactionType || '',
    title: initialData.title || '',
    titleEn: initialData.titleEn || '',
    description: initialData.description || '',
    descriptionEn: initialData.descriptionEn || '',
    price: initialData.price || '',
    area: initialData.area || '',
    bedrooms: initialData.bedrooms || '',
    bathrooms: initialData.bathrooms || '',
    parkingSpaces: initialData.parkingSpaces || '',
    floor: initialData.floor || '',
    totalFloors: initialData.totalFloors || '',
    buildYear: initialData.buildYear || '',
    ownershipType: initialData.ownershipType || '',
    furnishing: initialData.furnishing || '',
    emirate: initialData.emirate || '',
    area_name: initialData.area_name || '',
    address: initialData.address || '',
    googleMapsUrl: initialData.googleMapsUrl || '',
    amenities: initialData.amenities || [],
    serviceCharge: initialData.serviceCharge || '',
    rentFrequency: initialData.rentFrequency || 'yearly',
    permitNumber: initialData.permitNumber || '',
    ...initialData
  });
  
  // Handle form field change
  const handleFieldChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    onDataChange?.(newData);
  };
  
  // Handle amenity toggle
  const toggleAmenity = (amenityId) => {
    const currentAmenities = formData.amenities || [];
    const newAmenities = currentAmenities.includes(amenityId)
      ? currentAmenities.filter(a => a !== amenityId)
      : [...currentAmenities, amenityId];
    handleFieldChange('amenities', newAmenities);
  };
  
  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-blue-50 border-blue-200'}`}>
        <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          <Home className="h-5 w-5 text-blue-600" />
          {language === 'ar' ? 'المعلومات الأساسية' : 'Basic Information'}
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Property Type */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'نوع العقار *' : 'Property Type *'}
            </Label>
            <select
              value={formData.propertyType}
              onChange={(e) => handleFieldChange('propertyType', e.target.value)}
              className={`w-full h-10 rounded-md border px-3 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              data-testid="property-type-select"
            >
              <option value="">{language === 'ar' ? 'اختر نوع العقار' : 'Select Property Type'}</option>
              {propertyTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {language === 'ar' ? type.ar : type.en}
                </option>
              ))}
            </select>
          </div>
          
          {/* Transaction Type */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'نوع المعاملة *' : 'Transaction Type *'}
            </Label>
            <select
              value={formData.transactionType}
              onChange={(e) => handleFieldChange('transactionType', e.target.value)}
              className={`w-full h-10 rounded-md border px-3 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              data-testid="transaction-type-select"
            >
              <option value="">{language === 'ar' ? 'اختر نوع المعاملة' : 'Select Transaction Type'}</option>
              {transactionTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {language === 'ar' ? type.ar : type.en}
                </option>
              ))}
            </select>
          </div>
          
          {/* Title Arabic */}
          <div className="sm:col-span-2">
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'عنوان الإعلان (عربي) *' : 'Listing Title (Arabic) *'}
            </Label>
            <Input
              value={formData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder={language === 'ar' ? 'مثال: شقة فاخرة 3 غرف في دبي مارينا' : 'Example: Luxury 3BR Apartment in Dubai Marina'}
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              dir="rtl"
              data-testid="property-title-input"
            />
          </div>
          
          {/* Title English */}
          <div className="sm:col-span-2">
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'عنوان الإعلان (إنجليزي) *' : 'Listing Title (English) *'}
            </Label>
            <Input
              value={formData.titleEn}
              onChange={(e) => handleFieldChange('titleEn', e.target.value)}
              placeholder="Example: Luxury 3BR Apartment in Dubai Marina"
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              dir="ltr"
              data-testid="property-title-en-input"
            />
          </div>
        </div>
      </div>
      
      {/* Structural Details */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-white border-gray-200'}`}>
        <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          <Building2 className="h-5 w-5 text-cyan-600" />
          {language === 'ar' ? 'التفاصيل الهيكلية' : 'Structural Details'}
        </h3>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Area */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              <Ruler className="h-4 w-4 inline me-1" />
              {language === 'ar' ? 'المساحة (قدم²) *' : 'Area (sqft) *'}
            </Label>
            <Input
              type="number"
              value={formData.area}
              onChange={(e) => handleFieldChange('area', e.target.value)}
              placeholder="1500"
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="property-area-input"
            />
          </div>
          
          {/* Bedrooms */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              <Bed className="h-4 w-4 inline me-1" />
              {language === 'ar' ? 'غرف النوم *' : 'Bedrooms *'}
            </Label>
            <select
              value={formData.bedrooms}
              onChange={(e) => handleFieldChange('bedrooms', e.target.value)}
              className={`w-full h-10 rounded-md border px-3 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              data-testid="property-bedrooms-select"
            >
              <option value="">{language === 'ar' ? 'اختر' : 'Select'}</option>
              <option value="studio">{language === 'ar' ? 'استوديو' : 'Studio'}</option>
              {[1, 2, 3, 4, 5, 6, 7, '8+'].map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>
          
          {/* Bathrooms */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              <Bath className="h-4 w-4 inline me-1" />
              {language === 'ar' ? 'الحمامات *' : 'Bathrooms *'}
            </Label>
            <select
              value={formData.bathrooms}
              onChange={(e) => handleFieldChange('bathrooms', e.target.value)}
              className={`w-full h-10 rounded-md border px-3 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              data-testid="property-bathrooms-select"
            >
              <option value="">{language === 'ar' ? 'اختر' : 'Select'}</option>
              {[1, 2, 3, 4, 5, 6, '7+'].map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>
          
          {/* Parking */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'مواقف السيارات' : 'Parking'}
            </Label>
            <select
              value={formData.parkingSpaces}
              onChange={(e) => handleFieldChange('parkingSpaces', e.target.value)}
              className={`w-full h-10 rounded-md border px-3 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              data-testid="property-parking-select"
            >
              <option value="">{language === 'ar' ? 'اختر' : 'Select'}</option>
              {[0, 1, 2, 3, 4, '5+'].map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>
          
          {/* Floor */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'الطابق' : 'Floor'}
            </Label>
            <Input
              type="number"
              value={formData.floor}
              onChange={(e) => handleFieldChange('floor', e.target.value)}
              placeholder="5"
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="property-floor-input"
            />
          </div>
          
          {/* Total Floors */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'إجمالي الطوابق' : 'Total Floors'}
            </Label>
            <Input
              type="number"
              value={formData.totalFloors}
              onChange={(e) => handleFieldChange('totalFloors', e.target.value)}
              placeholder="20"
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="property-total-floors-input"
            />
          </div>
          
          {/* Build Year */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              <Calendar className="h-4 w-4 inline me-1" />
              {language === 'ar' ? 'سنة البناء' : 'Build Year'}
            </Label>
            <Input
              type="number"
              value={formData.buildYear}
              onChange={(e) => handleFieldChange('buildYear', e.target.value)}
              placeholder="2020"
              min="1980"
              max={new Date().getFullYear()}
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="property-build-year-input"
            />
          </div>
          
          {/* Furnishing */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'التأثيث' : 'Furnishing'}
            </Label>
            <select
              value={formData.furnishing}
              onChange={(e) => handleFieldChange('furnishing', e.target.value)}
              className={`w-full h-10 rounded-md border px-3 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              data-testid="property-furnishing-select"
            >
              <option value="">{language === 'ar' ? 'اختر' : 'Select'}</option>
              {furnishingOptions.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {language === 'ar' ? opt.ar : opt.en}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Legal & Ownership */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-amber-50 border-amber-200'}`}>
        <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          <Shield className="h-5 w-5 text-amber-600" />
          {language === 'ar' ? 'المعلومات القانونية' : 'Legal Information'}
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Ownership Type */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'نوع الملكية *' : 'Ownership Type *'}
            </Label>
            <select
              value={formData.ownershipType}
              onChange={(e) => handleFieldChange('ownershipType', e.target.value)}
              className={`w-full h-10 rounded-md border px-3 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              data-testid="property-ownership-select"
            >
              <option value="">{language === 'ar' ? 'اختر نوع الملكية' : 'Select Ownership Type'}</option>
              {ownershipTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {language === 'ar' ? type.ar : type.en}
                </option>
              ))}
            </select>
          </div>
          
          {/* Permit Number */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'رقم التصريح (RERA)' : 'Permit Number (RERA)'}
            </Label>
            <Input
              value={formData.permitNumber}
              onChange={(e) => handleFieldChange('permitNumber', e.target.value)}
              placeholder="1234567890"
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="property-permit-input"
            />
          </div>
        </div>
      </div>
      
      {/* Location */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-green-50 border-green-200'}`}>
        <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          <MapPin className="h-5 w-5 text-green-600" />
          {language === 'ar' ? 'الموقع' : 'Location'}
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Emirate */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'الإمارة *' : 'Emirate *'}
            </Label>
            <select
              value={formData.emirate}
              onChange={(e) => handleFieldChange('emirate', e.target.value)}
              className={`w-full h-10 rounded-md border px-3 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              data-testid="property-emirate-select"
            >
              <option value="">{language === 'ar' ? 'اختر الإمارة' : 'Select Emirate'}</option>
              {emirates.map(em => (
                <option key={em.id} value={em.id}>
                  {language === 'ar' ? em.ar : em.en}
                </option>
              ))}
            </select>
          </div>
          
          {/* Area Name */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'المنطقة *' : 'Area *'}
            </Label>
            <Input
              value={formData.area_name}
              onChange={(e) => handleFieldChange('area_name', e.target.value)}
              placeholder={language === 'ar' ? 'مثال: دبي مارينا' : 'Example: Dubai Marina'}
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="property-area-name-input"
            />
          </div>
          
          {/* Address */}
          <div className="sm:col-span-2">
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'العنوان التفصيلي' : 'Detailed Address'}
            </Label>
            <Input
              value={formData.address}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              placeholder={language === 'ar' ? 'المبنى، الشارع، المنطقة' : 'Building, Street, Area'}
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="property-address-input"
            />
          </div>
          
          {/* Google Maps URL */}
          <div className="sm:col-span-2">
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              <Map className="h-4 w-4 inline me-1" />
              {language === 'ar' ? 'رابط خرائط جوجل' : 'Google Maps URL'}
            </Label>
            <Input
              value={formData.googleMapsUrl}
              onChange={(e) => handleFieldChange('googleMapsUrl', e.target.value)}
              placeholder="https://maps.google.com/..."
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              dir="ltr"
              data-testid="property-maps-url-input"
            />
          </div>
        </div>
      </div>
      
      {/* Amenities */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-[#1E9BD9]/10 border-[#1E9BD9]'}`}>
        <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          <CheckSquare className="h-5 w-5 text-[#1E9BD9]" />
          {language === 'ar' ? 'المرافق والمميزات' : 'Amenities & Features'}
        </h3>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {amenities.map(amenity => (
            <button
              key={amenity.id}
              type="button"
              onClick={() => toggleAmenity(amenity.id)}
              className={`p-3 rounded-lg border-2 text-sm font-medium transition-all text-start ${
                formData.amenities?.includes(amenity.id)
                  ? 'border-[#1E9BD9] bg-[#1E9BD9]/20 text-[#1E9BD9]'
                  : isDarkMode
                    ? 'border-[#475569] hover:border-[#1E9BD9] text-gray-300'
                    : 'border-gray-200 hover:border-[#1E9BD9] text-gray-700'
              }`}
              data-testid={`amenity-${amenity.id}`}
            >
              {formData.amenities?.includes(amenity.id) && (
                <CheckSquare className="h-4 w-4 inline me-2 text-[#1E9BD9]" />
              )}
              {language === 'ar' ? amenity.ar : amenity.en}
            </button>
          ))}
        </div>
      </div>
      
      {/* Financial Details */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-red-50 border-red-200'}`}>
        <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          <DollarSign className="h-5 w-5 text-red-600" />
          {language === 'ar' ? 'التفاصيل المالية' : 'Financial Details'}
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Price */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'السعر (درهم) *' : 'Price (AED) *'}
            </Label>
            <Input
              type="number"
              value={formData.price}
              onChange={(e) => handleFieldChange('price', e.target.value)}
              placeholder="1500000"
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="property-price-input"
            />
          </div>
          
          {/* Rent Frequency (for rent properties) */}
          {(formData.transactionType === 'rent' || formData.transactionType === 'daily_rent') && (
            <div>
              <Label className={isDarkMode ? 'text-gray-300' : ''}>
                {language === 'ar' ? 'دورة الدفع' : 'Payment Frequency'}
              </Label>
              <select
                value={formData.rentFrequency}
                onChange={(e) => handleFieldChange('rentFrequency', e.target.value)}
                className={`w-full h-10 rounded-md border px-3 text-sm ${
                  isDarkMode 
                    ? 'bg-[#0F172A] border-[#334155] text-white' 
                    : 'bg-white border-gray-200 text-gray-900'
                }`}
                data-testid="property-rent-frequency-select"
              >
                <option value="yearly">{language === 'ar' ? 'سنوي' : 'Yearly'}</option>
                <option value="monthly">{language === 'ar' ? 'شهري' : 'Monthly'}</option>
                <option value="weekly">{language === 'ar' ? 'أسبوعي' : 'Weekly'}</option>
                <option value="daily">{language === 'ar' ? 'يومي' : 'Daily'}</option>
              </select>
            </div>
          )}
          
          {/* Service Charge */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'رسوم الخدمة السنوية (درهم)' : 'Annual Service Charge (AED)'}
            </Label>
            <Input
              type="number"
              value={formData.serviceCharge}
              onChange={(e) => handleFieldChange('serviceCharge', e.target.value)}
              placeholder="15000"
              className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
              data-testid="property-service-charge-input"
            />
          </div>
        </div>
      </div>
      
      {/* Description */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-white border-gray-200'}`}>
        <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          <FileText className="h-5 w-5 text-gray-600" />
          {language === 'ar' ? 'الوصف' : 'Description'}
        </h3>
        
        <div className="space-y-4">
          {/* Description Arabic */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'الوصف (عربي)' : 'Description (Arabic)'}
            </Label>
            <textarea
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder={language === 'ar' ? 'وصف تفصيلي للعقار...' : 'Detailed property description...'}
              rows={4}
              className={`w-full rounded-md border px-3 py-2 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              dir="rtl"
              data-testid="property-description-textarea"
            />
          </div>
          
          {/* Description English */}
          <div>
            <Label className={isDarkMode ? 'text-gray-300' : ''}>
              {language === 'ar' ? 'الوصف (إنجليزي)' : 'Description (English)'}
            </Label>
            <textarea
              value={formData.descriptionEn}
              onChange={(e) => handleFieldChange('descriptionEn', e.target.value)}
              placeholder="Detailed property description..."
              rows={4}
              className={`w-full rounded-md border px-3 py-2 text-sm ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-[#334155] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
              dir="ltr"
              data-testid="property-description-en-textarea"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
